import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react'

import createEngine, { DiagramModel, DefaultDiagramState, LinkModel, LinkModelGenerics, DiagramEngine } from '@projectstorm/react-diagrams'

import { BaseEntityEvent, BaseEvent, BaseModel, CanvasWidget } from '@projectstorm/react-canvas-core'

import dagre from 'dagre'

import Event, { DEFAULT_EVENT_END } from './../../../../../models/Event.model'

import { EventNodeFactory } from './graph-utils/event-node.factory'
import { EventNodeModel } from './graph-utils/event-node.model'
import { EventEndPortFactory } from './graph-utils/event-end-port/end-port.factory'
import { ActionsEnum } from '../../../../../utils/constants/action-types'
import { EventTypesEnum } from '../../../../../utils/constants/event-types'

import ZoomInIcon from '@material-ui/icons/ZoomInRounded'
import ZoomOutIcon from '@material-ui/icons/ZoomOutRounded'
import ZoomToFitIcon from '@material-ui/icons/ViewQuiltSharp'

import './graph.style.scss'
import { useStore } from '../../../../../stores/root.store'
import { debounce } from '@material-ui/core'

const GraphComponent: React.FC = observer(() => {
    const createStore = useStore('createStore')

    const [isLoading, setLoading] = useState(true)

    //1) setup the diagram engine
    const engine = useMemo(() => {
        const eng = createEngine()
        eng.getNodeFactories().registerFactory(new EventNodeFactory())
        eng.getPortFactories().registerFactory(new EventEndPortFactory())
        return eng
    }, [])

    //2) setup the diagram model
    const diagramModel = useMemo(() => new DiagramModel(), [])
    const eventNodes = useRef<{ [id: string]: EventNodeModel }>({}).current

    const clearGraph = useCallback(() => {
        const model = engine.getModel()
        if (model) {
            model.getNodes().forEach((n) => {
                n.clearListeners()
                model.removeNode(n)
            })
            model.getLinks().forEach((l) => model.removeLink(l))
        }
        Object.keys(eventNodes).forEach((key) => delete eventNodes[key])
    }, [engine, eventNodes])

    const initGraph = useCallback(
        (shouldZoomToFit: boolean = true) => {
            const { paths } = createStore
            const state = engine.getStateMachine().getCurrentState()
            if (state instanceof DefaultDiagramState) {
                state.dragNewLink.config.allowLooseLinks = false
            }

            // Create a new directed graph, configured to allow multiple edges (multigraph)
            const g = new dagre.graphlib.Graph({ multigraph: true })

            g.setGraph({
                rankdir: 'TB',
                ranker: 'longest-path',
                ranksep: 100
            })

            g.setDefaultEdgeLabel(() => ({}))

            // Add nodes and edges to the Dagre graph
            paths.forEach((path) => {
                path.events.forEach((e) => {
                    g.setNode(e._id, { e, color: path.color, width: 180, height: 80, pathId: path._id })

                    // Correctly loop through dependencies to create edges
                    e.dependencies.forEach((dependency) => {
                        dependency.availability.afterEvents?.forEach((ae) => {
                            // Provide a unique name for each edge to support multiple dependencies
                            // between the same two nodes.
                            const edgeName = `${e._id}-${ae.eventId}-${ae.endWith.value}`
                            g.setEdge(e._id, ae.eventId, { eventEnd: ae.endWith.value }, edgeName)
                        })
                    })
                })
            })

            dagre.layout(g)

            const links: LinkModel<LinkModelGenerics>[] = []
            const graphHeight = g.graph().height!

            g.nodes().forEach((v) => {
                const node = g.node(v)
                if (node) {
                    if (!eventNodes[v]) {
                        const event = (node as any).e as Event
                        let title = event.title
                        if (event.content.type === EventTypesEnum.Form) title += ' 📝'
                        eventNodes[v] = new EventNodeModel(title, (node as any).color, event._id, (node as any).pathId)
                    }
                    eventNodes[v].setPosition(node.x, graphHeight - node.y)
                }
            })

            g.edges().forEach((e) => {
                const edge = g.edge(e)

                try {
                    const eventEnd = (edge as any).eventEnd
                    const dependsOn = (g.node(e.w) as any).e as Event
                    let actionText = ''

                    if (eventEnd === undefined || eventEnd.length === 0 || eventEnd === DEFAULT_EVENT_END) {
                        actionText = 'any'
                    } else {
                        if (dependsOn!.content.type === EventTypesEnum.WithActions) {
                            if (typeof eventEnd === 'number') {
                                actionText = dependsOn!.content!.actions!.filter((a) => a.type === ActionsEnum.EndWith)[eventEnd]?.label
                            } else {
                                actionText = dependsOn!.content!.actions!.find((c) => eventEnd === c.id)?.label || 'N/A'
                            }
                        } else if (dependsOn!.content.type === EventTypesEnum.Checkbox) {
                            const checkboxLabels = dependsOn!
                                .content!.checkboxes!.filter((c) => eventEnd.includes(c.id))
                                .map((c) => c.label)
                                .join(' & ')
                            actionText = `☑️ ${checkboxLabels}`
                        }
                    }

                    const dependencyPort = eventNodes[e.v].addInPort('')
                    const eventEndPort = eventNodes[e.w].getPorts()[actionText] || eventNodes[e.w].addOutPort(actionText)

                    links.push(dependencyPort.link(eventEndPort))
                } catch (err) {
                    console.error('Error creating diagram link:', err)
                }
            })

            if (shouldZoomToFit) {
                setTimeout(() => {
                    zoomToFit()
                }, 500)
            }

            const models = diagramModel.addAll(...Object.values(eventNodes), ...links)
            diagramModel.setLocked(true)
            engine.setModel(diagramModel)
            setLoading(false)

            models.forEach((item) => {
                item.clearListeners() // Ensure no duplicate listeners
                item.registerListener({
                    selectionChanged: onSelectionChanged
                })
            })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [createStore, diagramModel, engine, eventNodes]
    )

    const expandAccordion = (id: string, attribute: 'event-id' | 'path-id') => {
        const element = document.querySelector(`[${attribute}="${id}"]`) as HTMLElement | null
        if (element) {
            const accordionButton = element.querySelector('.MuiAccordionSummary-root')
            if (accordionButton && !element.classList.contains('panelExpanded')) {
                ;(accordionButton as HTMLElement).click()
            }
        }
    }

    const togglePortLinksSelection = (ports: any[], isSelected: boolean) => {
        ports.forEach((port: any) => {
            Object.values(port.links).forEach((link: any) => {
                if ('setSelected' in link) {
                    link.setSelected(isSelected)
                }
            })
        })
    }

    const onSelectionChanged = useCallback((event: BaseEvent) => {
        const { entity } = event as BaseEntityEvent<BaseModel>

        // Check if the selected entity is an EventNodeModel before proceeding
        if (entity instanceof EventNodeModel) {
            expandAccordion(entity.eventId, 'event-id')
            expandAccordion(entity.pathId, 'path-id')

            const element = document.querySelector(`[event-id="${entity.eventId}"]`) as HTMLElement | null
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }, 100)
            }

            if (entity.getInPorts()?.length) {
                togglePortLinksSelection(entity.getInPorts(), entity.isSelected())
            }

            if (entity.getOutPorts()?.length) {
                togglePortLinksSelection(entity.getOutPorts(), entity.isSelected())
            }
        }
    }, [])

    useEffect(() => {
        const debouncedHandlePropsChange = debounce((fullPath: string) => {
            clearGraph()
            initGraph(false)

            const match = fullPath.match(/^(paths\/\d+\/events\/\d+)/)
            if (match) {
                const event = createStore.getPropertyByPath(match[1]) as Event
                const node = eventNodes[event._id]

                if (node) {
                    engine.getModel().clearSelection()
                    node.setSelected(true)
                    engine.zoomToFitNodes({ margin: 100, nodes: [node], maxZoom: 1 })
                }
            }
        }, 1000)

        createStore.listenToPropChange(debouncedHandlePropsChange)

        return () => {
            createStore.stopListenToPropChange(debouncedHandlePropsChange)
        }
    }, [clearGraph, createStore, engine, eventNodes, initGraph])

    useEffect(() => {
        clearGraph()
        initGraph()
    }, [clearGraph, initGraph])

    const zoomToFit = useCallback(() => engine.zoomToFit(), [engine])

    const zoomIn = useCallback(() => {
        const zoom = diagramModel.getZoomLevel()
        diagramModel.setZoomLevel(zoom + 5)
        engine.repaintCanvas()
    }, [diagramModel, engine])

    const zoomOut = useCallback(() => {
        const zoom = diagramModel.getZoomLevel()
        diagramModel.setZoomLevel(zoom - 5)
        engine.repaintCanvas()
    }, [diagramModel, engine])

    const [styles, setStyles] = useState<React.CSSProperties>({ top: 72, bottom: 0 })
    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY
            const windowHeight = window.innerHeight
            const bodyHeight = document.body.offsetHeight

            if (scrollY > 130 && scrollY + windowHeight < bodyHeight - 130) {
                setStyles({ top: 0, bottom: 'auto' })
            } else if (scrollY + windowHeight >= bodyHeight - 100) {
                setStyles({ top: 'auto', bottom: 100 })
            } else {
                setStyles({ top: 130, bottom: 0 })
            }
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    if (!engine.getModel()) {
        return <>Loading</>
    }
    return (
        <div className="graph-container" style={styles}>
            <div className="buttons">
                <div onClick={zoomToFit}>
                    <ZoomToFitIcon />
                </div>
                <div onClick={zoomIn}>
                    <ZoomInIcon />
                </div>
                <div onClick={zoomOut}>
                    <ZoomOutIcon />
                </div>
            </div>

            {isLoading ? <h2>Loading...</h2> : <CanvasWidget engine={engine} className="graph-container" />}
        </div>
    )
})

export default GraphComponent
